import { getLogger } from "@goodgamestudios/logger";
import { Power3 } from "gsap";
import { TweenMaxGroup } from "../helper/TweenMaxGroup";
import { Poker2Model } from "../../model/Poker2Model";
import { DispHelper } from "../helper/DispHelper";
import { ILobbyDetailComponent } from "./lobby/ILobbyDetailComponent";
import { Poker2Screen } from "./Poker2Screen";

import DisplayObject = createjs.DisplayObject;
import Container = createjs.Container;
import Event = createjs.Event;

const logger = getLogger("Poker2LobbyScreen");

export class Poker2LobbyScreen extends Poker2Screen {
    public static NAME: string = "Poker2LobbyScreen";

    private SLIDE_OUT_DURATION: number = 0.4;
    private SLIDE_IN_DURATION: number = 0.5;

    private _slotActiveX: number = 0;
    private _slotInactiveX: number = 280;
    private _slot1: Container;
    private _slot2: Container;
    private _detailHolder: Container;
    private _tsg_slide: TweenMaxGroup;
    private _avatarDetailView: AvatarLobbyDetailComponent;
    private _currentLobbyDetailVO: AbstractLobbyDetailVO;
    private _currentDetailComponent: ILobbyDetailComponent;

    constructor(disp: DisplayObject) {
        CONSTRUCTOR_HACK;
        super(disp);
    }

    protected init(): void {
        super.init();

        this._detailHolder = new Container();
        this._detailHolder.x = 19.7;
        this._detailHolder.y = 153.3;

        this._slot1 = new Container();
        this._slot2 = new Container();

        // add elements to view
        this.lobbyScreen.addChild(this._detailHolder);
        this._detailHolder.addChild(this._slot1);
        this._detailHolder.addChild(this._slot2);

        // avatar detail is always active and will be put always to slot1
        this._avatarDetailView = new AvatarLobbyDetailComponent(this._slot1);
        this._avatarDetailView.updateAvatar(Poker2Model.avatarData.myAvatar);

        // init values
        this.resetScreen();
    }

    private resetScreen(): void {
        this._slot2.x = this._slotInactiveX;
    }

    public show(): void {
        super.show();

        // add listeners
        this.controller.addEventListener("showTournamentDetail", this.bindFunction(this.onUpdateLobbyDetailView));
        this.controller.addEventListener("hideDetail", this.bindFunction(this.onHideDetail));
        this._detailHolder.addEventListener("hideDetail", this.bindFunction(this.onHideDetail));

        // enable avatar view
        this._avatarDetailView.enable();

        // enable and reset detail component
        if (this._currentDetailComponent) {
            this._currentDetailComponent.enable();
            this._currentDetailComponent.resetView();
        }

        this.resetScreen();
    }

    public hide(): void {
        super.hide();

        // remove listeners
        this.controller.removeEventListener("showTournamentDetail", this.bindFunction(this.onUpdateLobbyDetailView));
        this.controller.removeEventListener("hideDetail", this.bindFunction(this.onHideDetail));
        this._detailHolder.removeEventListener("hideDetail", this.bindFunction(this.onHideDetail));

        // stop tween
        this.disposeTweenGroup(this._tsg_slide);

        if (this._currentDetailComponent) {
            // disable component (not listening for events any more)
            this._currentDetailComponent.disable();
            this._currentDetailComponent.resetView();
        }
    }

    public destroy(): void {
        super.destroy();

        // remove listeners
        this.controller.removeEventListener("showTournamentDetail", this.bindFunction(this.onUpdateLobbyDetailView));
        this.controller.removeEventListener("hideDetail", this.bindFunction(this.onHideDetail));
        this._detailHolder.removeEventListener("hideDetail", this.bindFunction(this.onHideDetail));

        // stop tween
        this.disposeTweenGroup(this._tsg_slide);

        // kill avatar view
        this._avatarDetailView.destroy();
        this._avatarDetailView = null;

        // clear all detail components
        LobbyDetailComponentFactory.clearPool();
        this._currentDetailComponent = null;
    }

    protected onHideDetail(event: Event): void {
        this.slideOutSlot2();
    }

    protected onUpdateLobbyDetailView(event: Poker2LobbyDetailEvent): void {
        // sanity check
        if (!event.lobbyDetailVO) {
            logger.error("onUpdateLobbyDetailView(), event.lobbyDetailVO is null");
            return;
        }

        this._currentLobbyDetailVO = event.lobbyDetailVO;

        if (this._currentLobbyDetailVO.update || this._currentDetailComponent) {
            // just update detail view

            // slide in, if not visible
            var doTransition: boolean = true;
            if (this._slot2.x > this._slotActiveX) {
                doTransition = false;
                this.slideInSlot2();
            }

            this._currentDetailComponent && this._currentDetailComponent.update(this._currentLobbyDetailVO, doTransition);
        }
        else {
            // create new detail view and show it
            this.createLobbyDetailViewAndShow();
        }
    }

    private slideInSlot1(): void {
        this.disableDisp(this._slot1);

        // slide in slot1
        this.disposeTweenGroup(this._tsg_slide);
        this._tsg_slide = new TweenMaxGroup();
        this._tsg_slide.to(this._slot1, this.SLIDE_IN_DURATION, { x: this._slotActiveX, ease: Power3.easeOut, delay: 0.0, onComplete: this.bindFunction(this.onSlideInSlot1Complete) });
        this._tsg_slide.play();
    }

    private onSlideInSlot1Complete(tween: TweenMaxGroup = null): void {
        this.disposeTweenGroup(this._tsg_slide);
        this.enableDisp(this._slot1);
    }

    private slideOutSlot1(): void {
        // slide out slot1
        this.disposeTweenGroup(this._tsg_slide);
        this._tsg_slide = new TweenMaxGroup();
        this._tsg_slide.to(this._slot1, this.SLIDE_OUT_DURATION, { x: this._slotInactiveX, ease: Power3.easeOut, delay: 0.0, onComplete: this.bindFunction(this.onSlideOutSlot1Complete) });
        this._tsg_slide.play();
    }

    private onSlideOutSlot1Complete(): void {
        this.disposeTweenGroup(this._tsg_slide);
        this.createLobbyDetailViewAndShow();
    }

    private createLobbyDetailViewAndShow(): void {
        this.udpateLobbyDetailView();
        this.slideInSlot2();
    }

    private udpateLobbyDetailView(): void {
        DispHelper.removeAllChildrenOf(this._slot2);

        this._currentDetailComponent = LobbyDetailComponentFactory.getLobbyDetailComponent(this._currentLobbyDetailVO, this._slot2);
    }

    private slideInSlot2(): void {
        // slide in slot2
        this.disposeTweenGroup(this._tsg_slide);
        this._tsg_slide = new TweenMaxGroup();
        this._tsg_slide.to(this._slot2, this.SLIDE_IN_DURATION, { x: this._slotActiveX, ease: Power3.easeOut, delay: 0.0, onComplete: this.bindFunction(this.onSlideInSlot2Complete) });
        this._tsg_slide.play();
    }

    private onSlideInSlot2Complete(tween: TweenMaxGroup = null): void {
        this.disposeTweenGroup(this._tsg_slide);
        this.enableDisp(this._slot2);
    }

    private slideOutSlot2(): void {
        this.disableDisp(this._slot2);

        // slide out slot2
        this.disposeTweenGroup(this._tsg_slide);
        this._tsg_slide = new TweenMaxGroup();
        this._tsg_slide.to(this._slot2, this.SLIDE_OUT_DURATION, { x: this._slotInactiveX, ease: Power3.easeOut, delay: 0.0, onComplete: this.bindFunction(this.onSlideOutSlot2Comlete) });
        this._tsg_slide.play();
    }

    private onSlideOutSlot2Comlete(): void {
        this.disposeTweenGroup(this._tsg_slide);
    }

    public disposeTweenGroup(tween: TweenMaxGroup): void {
        if (!tween) {
            return;
        }

        tween.destroy();
        tween = null;
    }

    private disableDisp(disp: Container): void {
        disp.mouseChildren = false;
        disp.cacheAsBitmap = true;
    }

    private enableDisp(disp: Container): void {
        disp.mouseChildren = true;
        disp.cacheAsBitmap = false;
    }

    public get lobbyScreen(): Library.PokerInterface.PokerLobby {
        return this.disp as Library.PokerInterface.PokerLobby;
    }
}

import { AbstractLobbyDetailVO } from "../../gamevo/AbstractLobbyDetailVO";

import { Poker2LobbyDetailEvent } from "../../event/Poker2LobbyDetailEvent";

import { AvatarLobbyDetailComponent } from "./lobby/AvatarLobbyDetailComponent";
import { LobbyDetailComponentFactory } from "./lobby/LobbyDetailComponentFactory";
